import { useQuery } from '@tanstack/react-query';

import type { DiscountsTableRow, PaginatedResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { discountsEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * Search discounts parameters
 */
export interface SearchDiscountsParams {
	query?: string;
	limit?: number;
	page?: number;
	facets?: string[];
	filters?: {
		tabs?: string[];
		status?: string[];
		method?: string[];
		type?: string[];
		appliesTo?: string[];
		combinations?: string[];
	};
}

/**
 * API function to search discounts with optional filters and pagination
 * @param servicesProviderId - Required services provider ID
 * @param params - Optional search parameters
 */
export const searchDiscounts = async (
	servicesProviderId: string,
	params: SearchDiscountsParams = {},
): Promise<PaginatedResponse<DiscountsTableRow>> => {
	const response = await apiClient.post<PaginatedResponse<DiscountsTableRow>>(
		discountsEndpoints.search(servicesProviderId),
		params,
	);
	return response.data;
};

/**
 * Hook for searching discounts with optional filters and pagination
 */
export const useSearchDiscounts = (params: SearchDiscountsParams = {}) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.discounts.list(servicesProviderId, params).queryKey,
		queryFn: () => searchDiscounts(servicesProviderId, params),
		enabled: Boolean(servicesProviderId),
		staleTime: Infinity, // never stale unless the data is mutated
		...errorBoundaryOptions,
	});
};
